/**
 * Public host-facing API mounted on window.<Brand>Widget. Lets the embedding
 * page drive the widget: open it (optionally with a pre-filled / auto-sent
 * question) and close it. Reserved for a later chunk: identify(token, context).
 *
 * Design: docs/superpowers/specs/2026-05-27-widget-public-api-and-loader-design.md
 */

import { BRAND_SLUG, BRAND_NAME } from './brand.js';

/**
 * Where an open() call originated. 'search' = the <{brand}-search> element;
 * 'cta' = a host page element; 'nudge' = the proactive bubble (showNudge).
 * Named so the union is spelled once — every other file imports this type.
 */
export type ConversationSource = 'search' | 'cta' | 'nudge';

export interface KobotOpenOptions {
  /** Seed the conversation with this question. */
  message?: string;
  /** true (default) sends the message immediately; false pre-fills the composer. */
  autoSend?: boolean;
  /**
   * Where this open() call originated — attached to the conversation when a NEW
   * one starts (see chat-widget.ts ensureConversation). Undefined = unset (a
   * plain host embed / manual widget open), NOT the same as omitting the field
   * on the wire.
   */
  source?: ConversationSource;
}

/** Dane zalogowanego klienta sklepu — prefill zamówienia (bot nie pyta o dane). */
export interface KobotCustomerData {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Proactive bubble above the launcher, driven by the host page
 * (window.<Brand>Widget.nudge before load, or showNudge() after). Spec:
 * docs/superpowers/specs/2026-09-02-widget-nudge-design.md
 */
export interface KobotNudgeOptions {
  /** Session-level dedup key for the dismissal flag; defaults to a hash of `message`. */
  id?: string;
  /** Plain text shown in the bubble (≤ 300 chars). */
  message: string;
  /** Sent as the VISITOR's message when the bubble is clicked (≤ 1000 chars). Omit = just open. */
  query?: string;
  /** Delay in ms after boot before the bubble shows (default 2000, clamped to 0–60000). */
  delay?: number;
}

/** normalizeNudge() output: every field resolved. */
export interface NudgeSpec {
  id: string;
  message: string;
  query: string | null;
  delay: number;
}

export const NUDGE_MESSAGE_MAX = 300;
export const NUDGE_QUERY_MAX = 1000; // composer maxlength
export const NUDGE_ID_MAX = 64;
export const NUDGE_DEFAULT_DELAY_MS = 2000;
export const NUDGE_MAX_DELAY_MS = 60_000;

/** djb2 → base36. Only has to tell different messages apart, deterministically. */
function hashId(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = (Math.imul(h, 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

function warnNudge(reason: string): null {
  console.warn(`[${BRAND_NAME}] nudge ignored: ${reason}`);
  return null;
}

/**
 * Pure validation of host input (unknown → NudgeSpec | null). Invalid input is
 * rejected as a whole with a console.warn — the bubble never shows half-valid.
 */
export function normalizeNudge(raw: unknown): NudgeSpec | null {
  if (raw === null || typeof raw !== 'object') return warnNudge('expected an object');
  const { id, message, query, delay } = raw as Record<string, unknown>;
  const msg = typeof message === 'string' ? message.trim() : '';
  if (msg === '' || msg.length > NUDGE_MESSAGE_MAX) {
    return warnNudge(`"message" must be a non-empty string of at most ${NUDGE_MESSAGE_MAX} characters`);
  }
  const q = typeof query === 'string' ? query.trim() : '';
  if (q.length > NUDGE_QUERY_MAX) return warnNudge(`"query" must be at most ${NUDGE_QUERY_MAX} characters`);
  const explicitId = typeof id === 'string' ? id.trim() : '';
  if (explicitId.length > NUDGE_ID_MAX) return warnNudge(`"id" must be at most ${NUDGE_ID_MAX} characters`);
  let delayMs = NUDGE_DEFAULT_DELAY_MS;
  if (typeof delay === 'number' && Number.isFinite(delay)) {
    delayMs = Math.min(NUDGE_MAX_DELAY_MS, Math.max(0, delay));
  } else if (delay !== undefined) {
    // Same diagnostic policy as the other fields — but a bad delay only costs
    // the default, not the whole nudge.
    console.warn(`[${BRAND_NAME}] nudge: "delay" must be a number of milliseconds — using ${NUDGE_DEFAULT_DELAY_MS}`);
  }
  return { id: explicitId !== '' ? explicitId : hashId(msg), message: msg, query: q !== '' ? q : null, delay: delayMs };
}

export interface KobotWidgetApi {
  open(options?: KobotOpenOptions): void;
  close(): void;
  /**
   * Host page calls this with the logged-in shop user's data to shorten the
   * order flow. Server-side validated per-field; data is attached when the
   * conversation starts. NOT the HMAC identify() (reserved, v2) — plain
   * convenience prefill, the customer can still change any field in chat.
   */
  setCustomer(data: KobotCustomerData): void;
  /** Show a proactive bubble above the launcher (last call wins; see KobotNudgeOptions). */
  showNudge(options: KobotNudgeOptions): void;
}

/** Visibility inputs planOpen needs from the element. */
export interface WidgetVisibilityState {
  disabledByOwner: boolean;
  hiddenByUrlPattern: boolean;
}

export type OpenPlan =
  | { kind: 'skip' }
  | { kind: 'open'; forceVisible: boolean; send?: string; prefill?: string };

/**
 * Pure decision: given visibility + options, what should open() do?
 * - owner globally disabled the widget → skip (no-op)
 * - hidden only by URL pattern → open and force visible (explicit host intent wins)
 * - message + autoSend!==false → send; message + autoSend===false → prefill
 */
export function planOpen(state: WidgetVisibilityState, options: KobotOpenOptions): OpenPlan {
  if (state.disabledByOwner) return { kind: 'skip' };
  const forceVisible = state.hiddenByUrlPattern;
  const message = options.message?.trim();
  if (!message) return { kind: 'open', forceVisible };
  return options.autoSend === false
    ? { kind: 'open', forceVisible, prefill: message }
    : { kind: 'open', forceVisible, send: message };
}

// --- active-instance registry + readiness buffer -------------------------

/**
 * Minimal interface the registry needs. Decoupled from the full ChatWidget
 * class so this module stays framework-agnostic and typecheck-clean before
 * Task 2 adds the public open/close methods to ChatWidget.
 */
export interface WidgetInstance {
  open(options?: KobotOpenOptions): void | Promise<void>;
  close(): void;
  setCustomer(data: KobotCustomerData): void;
  showNudge(options: KobotNudgeOptions): void;
}

type PendingCall = (el: WidgetInstance) => void;

let active: WidgetInstance | null = null;
const pending: PendingCall[] = [];

export function registerWidgetInstance(el: WidgetInstance): void {
  active = el;
  if (pending.length > 0) {
    const calls = pending.splice(0, pending.length);
    for (const call of calls) call(el);
  }
}

export function unregisterWidgetInstance(el: WidgetInstance): void {
  if (active === el) active = null;
}

function run(call: PendingCall): void {
  if (active) call(active);
  else pending.push(call);
}

export function open(options?: KobotOpenOptions): void {
  run((el) => {
    void el.open(options);
  });
}

export function close(): void {
  run((el) => el.close());
}

export function setCustomer(data: KobotCustomerData): void {
  run((el) => el.setCustomer(data));
}

export function showNudge(options: KobotNudgeOptions): void {
  run((el) => el.showNudge(options));
}

// --- pre-load customer prefill ("window.<Brand>Widget.customer") ----------

/** Brand-aware global name, e.g. "KobotWidget" — shared with index.ts. */
export const GLOBAL_NAME = `${BRAND_SLUG.charAt(0).toUpperCase()}${BRAND_SLUG.slice(1)}Widget`;

/**
 * The embed loader is a `type=module` script (deferred), so a host inline
 * script runs BEFORE the bundle — setCustomer() wouldn't exist yet. The
 * pre-load convention is a plain property the host can set any time:
 *
 *   window.KobotWidget ||= {};
 *   window.KobotWidget.customer = { name, email, phone };
 *
 * Read lazily when the conversation starts; setCustomer() (post-load,
 * dynamic) overrides it per-field.
 */
export function readGlobalCustomer(): KobotCustomerData | null {
  const c = readGlobalProp('customer');
  if (c === null || c === undefined || typeof c !== 'object') return null;
  const { name, email, phone } = c as Record<string, unknown>;
  const out: KobotCustomerData = {
    ...(typeof name === 'string' ? { name } : {}),
    ...(typeof email === 'string' ? { email } : {}),
    ...(typeof phone === 'string' ? { phone } : {}),
  };
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Pre-load nudge convention (same reasoning as `customer`): the host sets
 * `window.<Brand>Widget.nudge = { id, message, query, delay }` any time before
 * the bundle runs; the element reads it once boot completes. Returned RAW —
 * normalizeNudge() validates. `undefined` = not set.
 */
export function readGlobalNudge(): unknown {
  return readGlobalProp('nudge');
}

/** Raw property of the pre-created brand global (`window.<Brand>Widget.<key>`); undefined when absent. */
function readGlobalProp(key: string): unknown {
  if (typeof window === 'undefined') return undefined;
  const g = (window as unknown as Record<string, unknown>)[GLOBAL_NAME];
  if (g === null || typeof g !== 'object') return undefined;
  return (g as Record<string, unknown>)[key];
}

/** Test-only: reset module state between specs. */
export function __resetRegistryForTests(): void {
  active = null;
  pending.length = 0;
}
